import { db } from '@/firebase/init'
import { AnalysisResult } from '@team-milva/lab-models/resources'
import type { IAnalysisResult } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Real analysis results (Apps/-lab-customer-/AnalysisResults) — MIL-338
// Phase 2. Per-order keyed subscription, same pattern as orderStore's
// subscribeToOrderSampleStatus, since results are only ever read for the one
// order currently being viewed, not preloaded app-wide.
export const useAnalysisResultStore = defineStore('analysisResult', {
  state: () => ({
    resultsByOrderID: {} as Record<string, AnalysisResult[]>,
    unsubscribeByOrderID: {} as Record<string, () => void>,
  }),

  actions: {
    subscribeToOrderResults(orderID: string): void {
      if (this.unsubscribeByOrderID[orderID]) return

      const q = query(
        collection(db, 'Apps', '-lab-customer-', 'AnalysisResults'),
        where('orderID', '==', orderID)
      )

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const results: AnalysisResult[] = []
        querySnapshot.forEach((docSnap) => {
          results.push(new AnalysisResult(docSnap.data() as IAnalysisResult))
        })
        this.resultsByOrderID = { ...this.resultsByOrderID, [orderID]: results }
      })

      this.unsubscribeByOrderID = { ...this.unsubscribeByOrderID, [orderID]: unsubscribe }
    },

    unsubscribeFromOrderResults(orderID: string): void {
      this.unsubscribeByOrderID[orderID]?.()

      const { [orderID]: _removedUnsubscribe, ...restUnsubscribe } = this.unsubscribeByOrderID
      this.unsubscribeByOrderID = restUnsubscribe

      const { [orderID]: _removedResults, ...restResults } = this.resultsByOrderID
      this.resultsByOrderID = restResults
    },

    resetStore(): void {
      Object.values(this.unsubscribeByOrderID).forEach((unsubscribe) => unsubscribe())
      this.unsubscribeByOrderID = {}
      this.resultsByOrderID = {}
    },
  },

  getters: {
    getResultsForOrder:
      (state) =>
      (orderID: string): AnalysisResult[] =>
        state.resultsByOrderID[orderID] ?? [],
  },
})
